import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { CRITERIA, IMAGES, COVERAGE } from "../mock";
import { SectionLabel } from "../components/Bits";

const PLEDGES = [
  { title: "Real reviewers. Actual visits.", body: "Every place we publish has been visited in person, anonymously, on our dime. No press trips, no comped meals." },
  { title: "Five criteria. One honest verdict.", body: "We judge every venue against the same five standards and publish a clear, opinionated recommendation." },
  { title: "No paid placements. Ever.", body: "We accept no advertising from venues we review. Revenue comes from readers, not restaurants." },
];

export default function About() {
  return (
    <div className="pt-[68px]">
      {/* Hero */}
      <section className="relative h-[56vh] min-h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0 kenburns">
          <img src={IMAGES.moody4} alt="Placeworthy" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 w-full pb-12">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-brass" />
            <span className="eyebrow text-brass">About Placeworthy</span>
          </div>
          <h1 className="font-serif text-paper text-4xl sm:text-5xl md:text-7xl font-semibold leading-[1.0] display-balance max-w-3xl">
            Independent by design. Honest by default.
          </h1>
        </div>
      </section>

      {/* Manifesto */}
      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-20">
        <p className="font-serif text-2xl sm:text-3xl md:text-4xl leading-snug display-balance">
          Five-star averages are broken. Google and Yelp ratings are gamed, inflated, and
          algorithmically anonymous. We built a better alternative.
        </p>
        <div className="grid md:grid-cols-2 gap-10 mt-12 text-lg text-ink-soft leading-[1.8]">
          <p>
            Placeworthy sends real reviewers to real places. We judge restaurants, hospitality venues,
            and local experiences against five honest criteria — food, atmosphere, service, value and
            overall experience — and publish verdicts you can actually trust.
          </p>
          <p>
            Founded in Calgary and built on an independent, entrepreneurial spirit, we are growing our
            catalogue city by city and country by country — across North America, Europe and the
            Asia–Pacific, and, in time, the whole world.
          </p>
        </div>
      </section>

      {/* Pledges */}
      <section className="bg-ink text-paper relative grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 relative">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-brass" />
            <span className="eyebrow text-brass">Our promise</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold display-balance max-w-2xl">
            Reviews you can rely on.
          </h2>
          <div className="grid md:grid-cols-3 gap-px bg-paper/10 mt-14 border border-paper/10">
            {PLEDGES.map((p) => (
              <div key={p.title} className="bg-ink p-8">
                <Check className="w-6 h-6 text-brass mb-5" />
                <h3 className="font-serif text-2xl font-semibold leading-tight">{p.title}</h3>
                <p className="text-paper/55 text-sm mt-3 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <SectionLabel>The methodology</SectionLabel>
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold mt-4 mb-12 display-balance max-w-2xl">
          Every venue, judged the same way.
        </h2>
        <div className="space-y-px bg-ink/10 border border-ink/10">
          {CRITERIA.map((c, i) => (
            <div key={c.key} className="bg-paper grid md:grid-cols-[80px_240px_1fr] gap-4 md:gap-8 items-start p-8 hover:bg-paper-dark transition-colors">
              <span className="font-serif text-4xl font-semibold text-claret">0{i + 1}</span>
              <h3 className="font-serif text-2xl font-semibold md:pt-1">{c.label}</h3>
              <p className="text-ink-soft leading-relaxed md:pt-2">{c.blurb}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Coverage + CTA */}
      <section className="bg-paper-dark relative grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 relative grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <SectionLabel>Where we are</SectionLabel>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold mt-4 display-balance">
              Independent reviews, expanding across the world.
            </h2>
            <div className="mt-8 space-y-3">
              {COVERAGE.map((c, i) => (
                <div key={c.name} className="flex items-center gap-5 py-4 border-b border-ink/12">
                  <span className="font-serif text-2xl font-semibold text-claret w-10">0{i + 1}</span>
                  <span className="font-serif text-xl font-semibold flex-1">{c.name}</span>
                  <span className="eyebrow text-ink-soft text-[10px] border border-ink/15 px-3 py-1.5">{c.note}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-ink text-paper p-10 lg:p-14 relative grain">
            <h3 className="font-serif text-2xl sm:text-3xl md:text-4xl font-semibold leading-tight display-balance">
              Be first to know when we launch.
            </h3>
            <p className="text-paper/60 mt-4 leading-relaxed">
              Reach out directly to get on our early-access list. We'll send a single note when the first
              reviews go live at placeworthy.ca.
            </p>
            <a href="mailto:placeworthy@gmail.com" className="inline-flex items-center gap-2 mt-8 px-7 py-4 bg-brass text-ink text-sm font-semibold tracking-wide hover:bg-paper transition-colors">
              placeworthy@gmail.com <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
